import { cva, type VariantProps } from 'class-variance-authority';
import { focusRing, uiMarker } from './shared';

/**
 * MEDIA — thumbnails and image frames.
 *
 * Two shapes, and the product drew both by hand. A *thumbnail* is a small
 * fixed-size picture in a row: the lot photo beside its title, four times as
 * `h-10 w-14 rounded-md border object-cover`, each with its own ImageOff
 * fallback beside it. A *media frame* is a picture that fills the width it is
 * given at a fixed aspect ratio, with something laid over it: a status badge
 * on a lot card, a check on a selected photo.
 */

const THUMB_SIZES = {
  landscape: { sm: 'h-10 w-14', md: 'h-14 w-20', lg: 'h-20 w-28' },
  square: { sm: 'size-10', md: 'size-14', lg: 'size-20' },
} as const;

export const thumbnailRecipe = cva(
  [
    uiMarker,
    'shrink-0 overflow-hidden rounded-md border border-border bg-muted',
  ],
  {
    variants: {
      size: { sm: '', md: '', lg: '' },
      shape: { landscape: '', square: '' },
    },
    compoundVariants: [
      { shape: 'landscape', size: 'sm', class: THUMB_SIZES.landscape.sm },
      { shape: 'landscape', size: 'md', class: THUMB_SIZES.landscape.md },
      { shape: 'landscape', size: 'lg', class: THUMB_SIZES.landscape.lg },
      { shape: 'square', size: 'sm', class: THUMB_SIZES.square.sm },
      { shape: 'square', size: 'md', class: THUMB_SIZES.square.md },
      { shape: 'square', size: 'lg', class: THUMB_SIZES.square.lg },
    ],
    defaultVariants: { size: 'sm', shape: 'landscape' },
  }
);

/**
 * The picture inside either container. Covers, never letterboxes — except in
 * a free-ratio frame, where the picture is shown whole at its own height:
 * the photo carousel's preview, the 404 page's artwork.
 */
export const mediaImageRecipe = cva([uiMarker, 'w-full'], {
  variants: {
    fit: {
      cover: 'h-full object-cover',
      contain: 'object-contain',
    },
    /** In a fixed-ratio frame the picture fills it; in a free one it sets the height. */
    ratio: {
      fixed: 'h-full',
      auto: 'h-auto',
    },
  },
  defaultVariants: { fit: 'cover', ratio: 'fixed' },
});
export type MediaImageRecipeProps = VariantProps<typeof mediaImageRecipe>;

/** What stands in for a picture there is not: a muted box with a glyph in it. */
export const mediaFallbackRecipe = cva([
  uiMarker,
  'flex h-full w-full items-center justify-center text-muted-foreground',
]);

/**
 * Aspect ratios written out, for the same reason Grid writes its columns out:
 * `aspect-[${ratio}]` compiles and produces no CSS.
 */
export const mediaFrameRecipe = cva(
  [uiMarker, 'relative w-full overflow-hidden'],
  {
    variants: {
      ratio: {
        /** No ratio: the frame is as tall as the picture. Pair with `fit="contain"`. */
        auto: '',
        '4/3': 'aspect-[4/3]',
        '3/2': 'aspect-[3/2]',
        '16/9': 'aspect-video',
        '16/11': 'aspect-[16/11]',
        '1/1': 'aspect-square',
      },
      radius: {
        none: 'rounded-none',
        /** For a frame at icon size — the map tile beside a place name. */
        sm: 'rounded-sm',
        md: 'rounded-md',
        lg: 'rounded-lg',
        xl: 'rounded-xl',
      },
      bordered: { true: 'border border-border', false: '' },
      /**
       * What shows behind the picture while it loads and beside it when it
       * does not fill the frame. `none` for artwork that must sit on the
       * page itself — a faded illustration, a preview whose parent already
       * draws the ground.
       */
      tone: {
        muted: 'bg-muted',
        none: '',
      },
    },
    defaultVariants: {
      ratio: '4/3',
      radius: 'none',
      bordered: false,
      tone: 'muted',
    },
  }
);

/**
 * A picture that opens something when pressed: a frame wrapped in a button.
 * `group` so the frame's picture can zoom a touch on hover. The radius
 * matches the frame's `lg`, so the focus ring follows its corners.
 */
export const mediaTileButtonRecipe = cva([
  uiMarker,
  'group block w-full cursor-pointer rounded-lg text-left',
  focusRing,
]);

/** Where the overlay goes. Top-right is where every badge in the app sits. */
export const mediaCornerRecipe = cva([
  uiMarker,
  'absolute right-2 top-2 flex items-center gap-1',
]);

export type ThumbnailRecipeProps = VariantProps<typeof thumbnailRecipe>;
export type MediaFrameRecipeProps = VariantProps<typeof mediaFrameRecipe>;
